/**
 * JS side of the design tokens (FE-005). Colour values live in tokens.css; this module holds the
 * WCAG contrast maths, the pairs every token combination must satisfy, and helpers for colours
 * that come from data (admins can configure custom issue statuses and colours, as in legacy).
 */

export const ISSUE_STATUSES = [
  'new',
  'feedback',
  'acknowledged',
  'confirmed',
  'assigned',
  'resolved',
  'closed',
] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

/** CSS variable for a legacy status (`status_enum_string` names). */
export const statusColorVar = (status: string): string =>
  (ISSUE_STATUSES as readonly string[]).includes(status)
    ? `var(--color-status-${status})`
    : 'var(--color-neutral-soft)';

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function parseHex(hex: string): [number, number, number] {
  const h = hex.trim().replace(/^#/, '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  if (!/^[0-9a-f]{6}$/i.test(full)) throw new Error(`not a hex colour: ${hex}`);
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
}

/** WCAG 2.1 relative luminance. */
export function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.1 contrast ratio (1–21). */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Text colour for an arbitrary (e.g. admin-configured) background: ink or white, whichever reads better. */
export function readableInk(background: string, ink = '#393939', inverse = '#ffffff'): string {
  return contrast(background, ink) >= contrast(background, inverse) ? ink : inverse;
}

/**
 * Every foreground/background combination the components use, with its WCAG requirement:
 * 4.5 = normal text (1.4.3), 3 = large text or UI component / focus indicator (1.4.11).
 */
export const CONTRAST_PAIRS: readonly { fg: string; bg: string; min: 3 | 4.5; use: string }[] = [
  { fg: 'ink', bg: 'surface', min: 4.5, use: 'body text' },
  { fg: 'ink', bg: 'canvas', min: 4.5, use: 'text on page background' },
  { fg: 'ink', bg: 'surface-muted', min: 4.5, use: 'breadcrumbs' },
  { fg: 'ink-muted', bg: 'surface', min: 4.5, use: 'hints, secondary text' },
  { fg: 'ink-muted', bg: 'surface-sunken', min: 4.5, use: 'table header' },
  { fg: 'ink-muted', bg: 'surface-raised', min: 4.5, use: 'card header meta' },
  { fg: 'ink-placeholder', bg: 'surface', min: 4.5, use: 'placeholders' },
  { fg: 'heading', bg: 'surface', min: 4.5, use: 'page titles' },
  { fg: 'link', bg: 'surface', min: 4.5, use: 'links' },
  { fg: 'link', bg: 'surface-muted', min: 4.5, use: 'links in breadcrumbs / card footers' },
  { fg: 'link', bg: 'surface-sunken', min: 4.5, use: 'links on sunken surfaces' },
  { fg: 'link', bg: 'surface-raised', min: 4.5, use: 'links in card headers' },
  { fg: 'link', bg: 'primary-soft', min: 4.5, use: 'links on primary-soft' },
  { fg: 'link-hover', bg: 'surface', min: 4.5, use: 'links (hover)' },
  { fg: 'link-hover', bg: 'canvas', min: 4.5, use: 'links on the page background (public layout)' },
  { fg: 'primary-ink', bg: 'surface-raised', min: 4.5, use: 'status page heading' },
  { fg: 'link-hover', bg: 'surface-hover', min: 4.5, use: 'pagination / table row hover' },
  { fg: 'primary-ink', bg: 'surface-raised', min: 4.5, use: 'card title' },
  { fg: 'ink-inverse', bg: 'primary', min: 4.5, use: 'primary button' },
  { fg: 'ink-inverse', bg: 'primary-hover', min: 4.5, use: 'primary button (hover)' },
  { fg: 'ink-inverse', bg: 'danger', min: 4.5, use: 'danger button' },
  { fg: 'ink-inverse', bg: 'danger-hover', min: 4.5, use: 'danger button (hover)' },
  { fg: 'ink-inverse', bg: 'success', min: 4.5, use: 'success button / toast' },
  { fg: 'ink-inverse', bg: 'neutral', min: 4.5, use: 'neutral badge' },
  { fg: 'ink', bg: 'warning', min: 4.5, use: 'warning button' },
  { fg: 'ink', bg: 'neutral-soft', min: 4.5, use: 'secondary button (hover)' },
  { fg: 'danger-ink', bg: 'danger-soft', min: 4.5, use: 'danger alert' },
  { fg: 'danger-ink', bg: 'surface', min: 4.5, use: 'field error message' },
  { fg: 'success-ink', bg: 'success-soft', min: 4.5, use: 'success alert' },
  { fg: 'success-ink', bg: 'surface', min: 4.5, use: 'field success message' },
  { fg: 'warning-ink', bg: 'warning-soft', min: 4.5, use: 'warning alert' },
  { fg: 'info-ink', bg: 'info-soft', min: 4.5, use: 'info alert' },
  { fg: 'ink-inverse', bg: 'navbar', min: 4.5, use: 'navbar' },
  { fg: 'ink-inverse', bg: 'navbar-hover', min: 4.5, use: 'navbar (hover)' },
  { fg: 'sidebar-ink', bg: 'sidebar-item', min: 4.5, use: 'sidebar item' },
  { fg: 'sidebar-ink-hover', bg: 'sidebar-item-hover', min: 4.5, use: 'sidebar item (hover)' },
  { fg: 'sidebar-active-ink', bg: 'sidebar-active', min: 4.5, use: 'sidebar current page' },
  { fg: 'border-strong', bg: 'surface', min: 3, use: 'input / checkbox boundary' },
  { fg: 'danger', bg: 'surface', min: 3, use: 'invalid input boundary' },
  { fg: 'success', bg: 'surface', min: 3, use: 'valid input boundary' },
  { fg: 'focus', bg: 'surface', min: 3, use: 'focus ring' },
  { fg: 'focus', bg: 'canvas', min: 3, use: 'focus ring on page background' },
  { fg: 'focus', bg: 'surface-muted', min: 3, use: 'focus ring in breadcrumbs' },
  { fg: 'focus', bg: 'sidebar-item', min: 3, use: 'focus ring in sidebar' },
  { fg: 'focus-inverse', bg: 'navbar', min: 3, use: 'focus ring on navbar' },
  { fg: 'primary', bg: 'surface', min: 3, use: 'checkbox checked / spinner' },
  ...ISSUE_STATUSES.map((s) => ({
    fg: 'link-hover',
    bg: `status-${s}`,
    min: 4.5 as const,
    use: `link on status-${s} row (.on-tint)`,
  })),
  ...ISSUE_STATUSES.map((s) => ({
    fg: 'ink',
    bg: `status-${s}`,
    min: 4.5 as const,
    use: `status ${s}`,
  })),
];
